import { useMemo } from 'react';

import { requestGQL } from '../../../common/graphQlClient';
import { PremiseOnSinglePremise } from '../../premises/Premise';
import { GET_OUTLETS, GetOutletsResponse } from '../api';
import { useDeleteOutletPhoto } from './useDeleteOutletPhoto';
import { useMutateOutletPhoto } from './useMutateOutletPhoto';
import { useConnection } from '../../connection/hooks/useConnection';

export const outletsKey = (propertyId: number | null) => ['propertyConnection', propertyId] as const;
export const fetchOutlets = (propertyId: number | null) => () =>
  requestGQL<GetOutletsResponse>(GET_OUTLETS, { id: propertyId });

export const usePremiseOutlets = (premise: PremiseOnSinglePremise | null) => {
  const propertyId = useMemo(() => {
    return premise?.propertyId ?? null;
  }, [premise]);

  const { outlets, isError: isErrorOutlets, isLoading: isLoadingOutlets, isRefetching } = useConnection(propertyId);

  const { deletePhoto, isSuccess: isSuccessDeletePhoto, isLoading: isDeletingPhoto } = useDeleteOutletPhoto(propertyId);

  // Use offline-aware mutation hook
  const { mutate } = useMutateOutletPhoto(propertyId);

  const premiseOutlets = useMemo(
    () => (premise ? outlets.filter(({ premiseId }) => premiseId === premise.id) : []),
    [outlets, isRefetching, premise?.id],
  );

  const updateOutletPhoto = (outletId: string, photoUrl: string) => {
    if (propertyId === null) return;
    mutate({
      propertyId,
      outletId,
      photoUrl,
    });
  };

  return {
    isLoadingOutlets,
    isErrorOutlets,
    outlets: premiseOutlets,
    updateOutletPhoto,
    deletePhoto,
    isSuccessDeletePhoto,
    isDeletingPhoto,
  };
};
